import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";

const lineVariants = {
  hidden: { y: "110%" },
  visible: {
    y: "0%",
    transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1] },
  },
};

const container = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.14, delayChildren: 0.3 },
  },
};

const HeroLine = ({ children, className = "" }) => (
  <span className="line-mask">
    <motion.span variants={lineVariants} className={`block ${className}`}>
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const blobY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-[100vh] flex flex-col justify-center overflow-hidden pt-28 pb-16"
      data-testid="hero-section"
    >
      {/* Parallax background */}
      <motion.div
        style={{ y: blobY }}
        className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#00D2D3]/20 blur-[120px] pointer-events-none"
      />
      <motion.div
        style={{ y: blobY }}
        className="absolute top-1/3 -left-40 w-[500px] h-[500px] rounded-full bg-[#00D2D3]/10 blur-[130px] pointer-events-none"
      />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(0,210,211,0.08),transparent_60%)] pointer-events-none" />

      <motion.div
        style={{ y: contentY, opacity }}
        className="relative z-10 max-w-7xl w-full mx-auto px-6 lg:px-10"
      >
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.6 }}
          className="font-mono-accent text-xs sm:text-sm text-[#00D2D3] tracking-[0.25em] uppercase mb-6"
          data-testid="hero-overline"
        >
          Akuant Solutions · Tecnologia sob medida
        </motion.p>

        <motion.h1
          variants={container}
          initial="hidden"
          animate="visible"
          className="font-outfit font-black text-4xl sm:text-6xl lg:text-[5.2rem] leading-[1.02] tracking-tight text-white max-w-5xl"
          data-testid="hero-headline"
        >
          <HeroLine>Sistemas sob medida.</HeroLine>
          <HeroLine className="text-gradient-teal">
            Resultados de verdade.
          </HeroLine>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 text-base sm:text-lg text-white/60 max-w-xl leading-relaxed font-manrope"
          data-testid="hero-subheadline"
        >
          Chega de processos manuais. Desenvolvemos sistemas, landing pages
          de alta conversão e automações para negócios que querem crescer
          de verdade.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Button
            asChild
            size="lg"
            className="bg-[#00D2D3] text-black hover:bg-[#00D2D3]/90 rounded-full px-7 h-12 font-manrope font-semibold transition-all duration-300 hover:shadow-[0_0_30px_rgba(0,210,211,0.5)] group"
            data-testid="hero-form-cta"
          >
            <a href="#contato">
              Iniciar Projeto
              <ArrowUpRight className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </Button>
          <a
            href="#cases"
            className="text-sm font-manrope font-medium text-white/80 hover:text-white border border-white/15 hover:border-[#00D2D3]/50 rounded-full px-6 h-12 flex items-center transition-all duration-300"
            data-testid="hero-view-cases-link"
          >
            Ver Cases
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="mt-16 flex flex-wrap gap-x-12 gap-y-4 border-t border-white/10 pt-8 max-w-xl"
          data-testid="hero-stats"
        >
          <div>
            <p className="font-outfit font-bold text-3xl text-white">15+</p>
            <p className="text-xs text-white/50 font-manrope uppercase tracking-wide mt-1">
              Projetos entregues
            </p>
          </div>
          <div>
            <p className="font-outfit font-bold text-3xl text-white">
              21 dias
            </p>
            <p className="text-xs text-white/50 font-manrope uppercase tracking-wide mt-1">
              Entrega em dias úteis
            </p>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40"
      >
        <ArrowDown className="h-4 w-4 animate-bounce" />
      </motion.div>
    </section>
  );
};

export default Hero;
